import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import type { SelectedAsset } from '@dutchie/common/components/asset-library-modal';

import { resolveImageSrc } from '../../image-src';

// Lazy so `AssetLibraryModal`, its GraphQL documents, and Apollo (all pulled in transitively via
// `use-enterprise-asset-library.ts`) stay out of the storefront bundle -- see asset-picker-modal.tsx
// for why. Both `asset-picker-field.tsx` (rendered by `image.tsx`) and `background-field.tsx`
// (rendered by `section.tsx`/`flex-layout.tsx`) are reachable from the production renderer, so a
// static import here would ship the whole asset browser + an Apollo query hook to every shopper
// who views a published CMS page, even though only the editor can ever open it. Moved here (out
// of `asset-picker-field.tsx`) so both call sites share exactly one lazy boundary, rather than
// each needing its own -- see `bundle-boundaries.test.ts`'s static-only walk, which is what
// actually enforces that this stays a dynamic import.
const AssetPickerModal = lazy(() => import('./asset-picker-modal'));

export type UseAssetPickerResult = {
  /** True while the picker modal is open (i.e. mounted). */
  isOpen: boolean;
  /** Opens the picker modal. No-op (never mounts anything) when there is no enterprise in scope. */
  handleOpen: () => void;
  handleClose: () => void;
  /**
   * The lazily-loaded modal, Suspense-wrapped -- `null` until opened, and always `null` with no
   * enterprise in scope. Render this once, anywhere in the consuming widget's tree.
   */
  modal: JSX.Element | null;
};

/**
 * Shared "browse the asset library" behaviour used by both Library / URL tab controls without
 * pulling `asset-picker-field.tsx`'s own `FieldLabel` wrapper into `background-field.tsx`.
 *
 * Deliberately owns the pick-time `resolveImageSrc` conversion too (not just open/close state):
 * both call sites store the exact same bare-URL-string shape (`AssetPickerField`'s `onChange`
 * contract), so there is nothing caller-specific left to do with a freshly picked asset beyond
 * what this hook already does -- see `AssetPickerFieldProps.onChange`'s doc comment on
 * `asset-picker-field.tsx` for why the *manual* URL input must NOT go through this same
 * conversion (that responsibility stays with each caller's own text input, untouched here).
 *
 * `enterpriseId` is read from `CmsEnterpriseContext` by the *caller*, not here -- this hook takes
 * it as a plain parameter so it has no context/router dependency of its own to keep in sync.
 */
export function useAssetPicker(
  enterpriseId: string | undefined,
  onChange: (value: string) => void,
  onAssetSelect?: (value: string, asset: SelectedAsset) => void,
  disabled = false
): UseAssetPickerResult {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = useCallback(() => {
    if (!disabled) setIsOpen(true);
  }, [disabled]);
  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  // See asset-picker-field.tsx's original handleSelect doc comment (now here) for why this
  // conversion happens at pick time rather than being left to render time.
  const handleSelect = useCallback(
    (asset: SelectedAsset) => {
      if (disabled) {
        setIsOpen(false);
        return;
      }
      const resolvedSrc = resolveImageSrc(asset.url);
      if (onAssetSelect) onAssetSelect(resolvedSrc, asset);
      else onChange(resolvedSrc);
      setIsOpen(false);
    },
    [disabled, onAssetSelect, onChange]
  );

  const modal =
    isOpen && enterpriseId ? (
      <Suspense fallback={null}>
        <AssetPickerModal enterpriseId={enterpriseId} onSelect={handleSelect} onClose={handleClose} />
      </Suspense>
    ) : null;

  return { isOpen, handleOpen, handleClose, modal };
}

const BrowseButton = styled.button`
  background: var(--dcm-cms-image-source-action-bg, var(--puck-color-white, #fff));
  border: 1px solid var(--dcm-cms-image-source-action-border, var(--puck-color-border, #d0d4d8));
  border-radius: var(--dcm-cms-image-source-action-radius, 4px);
  color: var(--dcm-cms-image-source-action-color, var(--puck-color-text, #181b1f));
  cursor: pointer;
  font-size: 14px;
  padding: 7px 8px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

export type AssetPickerBrowseButtonProps = {
  enterpriseId: string | undefined;
  onClick: () => void;
  disabled?: boolean;
  testId: string;
  /** Merged over the shared base button style, for callers with their own row layout. */
  style?: React.CSSProperties;
};

/**
 * "Browse library" button, hidden entirely with no enterprise in scope -- the same degrade
 * `asset-picker-field.tsx` already applied inline; extracted here so `background-field.tsx`
 * matches it exactly rather than re-deriving the same condition.
 */
export function AssetPickerBrowseButton({
  enterpriseId,
  onClick,
  disabled,
  testId,
  style,
}: AssetPickerBrowseButtonProps): JSX.Element | null {
  // No enterprise in scope means no library to browse -- the caller's own URL input still works.
  if (!enterpriseId) return null;

  return (
    <BrowseButton type='button' onClick={onClick} disabled={disabled} data-testid={testId} style={style}>
      Browse library
    </BrowseButton>
  );
}
